<template>
  <b-container fluid class="py-4">
    <!-- Header and Filters -->
    <b-card no-body class="mb-4 shadow-sm rounded-lg">
      <b-card-header class="bg-white border-0 d-flex justify-content-between align-items-center py-3">
        <h3 class="mb-0 font-weight-bold text-dark">Senarai Inisiatif</h3>
        <b-button v-if="user && user.peranan && user.peranan !== 'admin' && filteredProjects.length > 0"
          variant="primary" class="px-4 py-2 rounded-pill" @click="$router.push('/projek/add')">
          <i class="fas fa-plus mr-2"></i> Tambah Inisiatif
        </b-button>
      </b-card-header>
      <!-- Filters -->
      <b-card-body class="py-3">
        <!-- Baris 1: Nama Inisiatif dan Status Penilaian (selalu ada) -->
        <b-row align-v="center" class="mb-3">
          <b-col md="6">
            <b-form-group label="Nama Inisiatif" label-class="font-weight-bold text-muted">
              <b-input-group>
                <b-form-input id="project-name-filter" v-model="projectNameFilter" placeholder="Cari nama Inisiatif..."
                  class="rounded-pill filter-boom" debounce="300" @input="filterProjects"></b-form-input>
                <b-input-group-append v-if="projectNameFilter">
                  <b-button variant="link" class="clear-btn" @click="clearProjectNameFilter">
                    <i class="fas fa-times-circle text-danger"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6" class="mb-3 mb-md-0">
            <b-form-group label="Status Penilaian" label-class="font-weight-bold text-muted">
              <b-form-select id="status-filter" v-model="selectedStatus" :options="filteredStatusOptions"
                class="custom-select rounded-pill filter-boom" @change="filterProjects">
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Baris 2: Jenis Agensi dan conditional field -->
        <b-row v-if="user && user.peranan === 'admin'" align-v="center" class="mb-3">
          <b-col md="6" class="mb-3 mb-md-0">
            <b-form-group label="Jenis Organisasi" label-class="font-weight-bold text-muted">
              <b-form-select id="agency-type-filter" v-model="agencyType" :options="agencyTypeOptions"
                class="custom-select rounded-pill filter-boom" @change="handleAgencyTypeChange">
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6" class="mb-3 mb-md-0">
            <!-- Default: Bulan/Tahun -->
            <b-form-group v-if="!agencyType" label="Bulan/Tahun (mm/yyyy)" label-class="font-weight-bold text-muted">
              <div class="position-relative">
                <b-form-input v-model="monthYearDisplay" placeholder="Pilih bulan dan tahun" readonly
                  class="rounded-pill filter-boom" @click="showMonthYearPicker = !showMonthYearPicker"></b-form-input>
                <b-icon-calendar class="calendar-icon"
                  @click="showMonthYearPicker = !showMonthYearPicker"></b-icon-calendar>
                <b-card v-if="showMonthYearPicker" class="month-year-picker shadow-sm">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <b-button variant="link" class="p-0" @click="prevYear">
                      <b-icon-chevron-left></b-icon-chevron-left>
                    </b-button>
                    <h6 class="mb-0">{{ tempSelectedYear }}</h6>
                    <b-button variant="link" class="p-0" @click="nextYear">
                      <b-icon-chevron-right></b-icon-chevron-right>
                    </b-button>
                  </div>
                  <div class="month-grid">
                    <b-button v-for="(month, index) in months" :key="index" @click="selectMonth(index + 1)"
                      :variant="isMonthSelected(index + 1) ? 'primary' : 'outline-secondary'" class="month-button"
                      size="sm">
                      {{ month }}
                    </b-button>
                  </div>
                  <div class="d-flex justify-content-end mt-3">
                    <b-button variant="link" size="sm" class="text-danger" @click="clearMonthYearFilter">
                      <b-icon-x></b-icon-x> Kosongkan
                    </b-button>
                  </div>
                </b-card>
              </div>
            </b-form-group>

            <!-- Jika kerajaan: Kementerian -->
            <b-form-group v-else-if="agencyType === 'kerajaan'" label="Organisasi"
              label-class="font-weight-bold text-muted">
              <b-form-select id="ministry-filter" v-model="selectedMinistry" :options="ministryOptions"
                class="custom-select rounded-pill filter-boom" @change="handleMinistryChange">
              </b-form-select>
            </b-form-group>

            <!-- Jika swasta: Agensi -->
            <b-form-group v-else-if="agencyType === 'swasta'" label="Agensi" label-class="font-weight-bold text-muted">
              <b-form-select id="agency-filter-swasta" v-model="selectedAgency" :options="availableAgencies"
                class="custom-select rounded-pill filter-boom" :disabled="isAgencyFilterDisabled"
                @change="filterProjects">
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Baris 3: Conditional fields untuk jenis agensi tertentu -->
        <b-row v-if="user && user.peranan === 'admin' && agencyType" align-v="center" class="mb-3">
          <!-- Jika kerajaan: Agensi dan Bulan/Tahun -->
          <template v-if="agencyType === 'kerajaan'">
            <b-col md="6" class="mb-3 mb-md-0">
              <b-form-group label="Agensi" label-class="font-weight-bold text-muted">
                <b-form-select id="agency-filter" v-model="selectedAgency" :options="availableAgencies"
                  class="custom-select rounded-pill filter-boom" :disabled="isAgencyFilterDisabled"
                  @change="filterProjects">
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6" class="mb-3 mb-md-0">
              <b-form-group label="Bulan/Tahun (mm/yyyy)" label-class="font-weight-bold text-muted">
                <div class="position-relative">
                  <b-form-input v-model="monthYearDisplay" placeholder="Pilih bulan dan tahun" readonly
                    class="rounded-pill filter-boom" @click="showMonthYearPicker = !showMonthYearPicker"></b-form-input>
                  <b-icon-calendar class="calendar-icon"
                    @click="showMonthYearPicker = !showMonthYearPicker"></b-icon-calendar>
                  <b-card v-if="showMonthYearPicker" class="month-year-picker shadow-sm">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <b-button variant="link" class="p-0" @click="prevYear">
                        <b-icon-chevron-left></b-icon-chevron-left>
                      </b-button>
                      <h6 class="mb-0">{{ tempSelectedYear }}</h6>
                      <b-button variant="link" class="p-0" @click="nextYear">
                        <b-icon-chevron-right></b-icon-chevron-right>
                      </b-button>
                    </div>
                    <div class="month-grid">
                      <b-button v-for="(month, index) in months" :key="index" @click="selectMonth(index + 1)"
                        :variant="isMonthSelected(index + 1) ? 'primary' : 'outline-secondary'" class="month-button"
                        size="sm">
                        {{ month }}
                      </b-button>
                    </div>
                    <div class="d-flex justify-content-end mt-3">
                      <b-button variant="link" size="sm" class="text-danger" @click="clearMonthYearFilter">
                        <b-icon-x></b-icon-x> Kosongkan
                      </b-button>
                    </div>
                  </b-card>
                </div>
              </b-form-group>
            </b-col>
          </template>

          <!-- Jika swasta: Bulan/Tahun sahaja -->
          <template v-else-if="agencyType === 'swasta'">
            <b-col md="6" class="mb-3 mb-md-0">
              <b-form-group label="Bulan/Tahun (mm/yyyy)" label-class="font-weight-bold text-muted">
                <div class="position-relative">
                  <b-form-input v-model="monthYearDisplay" placeholder="Pilih bulan dan tahun" readonly
                    class="rounded-pill filter-boom" @click="showMonthYearPicker = !showMonthYearPicker"></b-form-input>
                  <b-icon-calendar class="calendar-icon"
                    @click="showMonthYearPicker = !showMonthYearPicker"></b-icon-calendar>
                  <b-card v-if="showMonthYearPicker" class="month-year-picker shadow-sm">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <b-button variant="link" class="p-0" @click="prevYear">
                        <b-icon-chevron-left></b-icon-chevron-left>
                      </b-button>
                      <h6 class="mb-0">{{ tempSelectedYear }}</h6>
                      <b-button variant="link" class="p-0" @click="nextYear">
                        <b-icon-chevron-right></b-icon-chevron-right>
                      </b-button>
                    </div>
                    <div class="month-grid">
                      <b-button v-for="(month, index) in months" :key="index" @click="selectMonth(index + 1)"
                        :variant="isMonthSelected(index + 1) ? 'primary' : 'outline-secondary'" class="month-button"
                        size="sm">
                        {{ month }}
                      </b-button>
                    </div>
                    <div class="d-flex justify-content-end mt-3">
                      <b-button variant="link" size="sm" class="text-danger" @click="clearMonthYearFilter">
                        <b-icon-x></b-icon-x> Kosongkan
                      </b-button>
                    </div>
                  </b-card>
                </div>
              </b-form-group>
            </b-col>
            <!-- Kolom kosong untuk menjaga susunan grid -->
            <b-col md="6" class="mb-3 mb-md-0"></b-col>
          </template>
        </b-row>

        <!-- Baris 2: Untuk Pentadbir - Agensi dan Bulan/Tahun (kekal seperti asal) -->
        <b-row v-if="user && user.peranan === 'pentadbir'" align-v="center" class="mb-3">
          <b-col md="6" class="mb-3 mb-md-0">
            <b-form-group label="Agensi" label-class="font-weight-bold text-muted">
              <b-form-select id="agency-filter" v-model="selectedAgency" :options="availableAgencies"
                class="custom-select rounded-pill filter-boom" @change="filterProjects">
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6" class="mb-3 mb-md-0">
            <b-form-group label="Bulan/Tahun (mm/yyyy)" label-class="font-weight-bold text-muted">
              <div class="position-relative">
                <b-form-input v-model="monthYearDisplay" placeholder="Pilih bulan dan tahun" readonly
                  class="rounded-pill filter-boom" @click="showMonthYearPicker = !showMonthYearPicker"></b-form-input>
                <b-icon-calendar class="calendar-icon"
                  @click="showMonthYearPicker = !showMonthYearPicker"></b-icon-calendar>
                <b-card v-if="showMonthYearPicker" class="month-year-picker shadow-sm">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <b-button variant="link" class="p-0" @click="prevYear">
                      <b-icon-chevron-left></b-icon-chevron-left>
                    </b-button>
                    <h6 class="mb-0">{{ tempSelectedYear }}</h6>
                    <b-button variant="link" class="p-0" @click="nextYear">
                      <b-icon-chevron-right></b-icon-chevron-right>
                    </b-button>
                  </div>
                  <div class="month-grid">
                    <b-button v-for="(month, index) in months" :key="index" @click="selectMonth(index + 1)"
                      :variant="isMonthSelected(index + 1) ? 'primary' : 'outline-secondary'" class="month-button"
                      size="sm">
                      {{ month }}
                    </b-button>
                  </div>
                  <div class="d-flex justify-content-end mt-3">
                    <b-button variant="link" size="sm" class="text-danger" @click="clearMonthYearFilter">
                      <b-icon-x></b-icon-x> Kosongkan
                    </b-button>
                  </div>
                </b-card>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5 loading-container">
      <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;"></b-spinner>
      <p class="mt-3 text-muted font-weight-medium">Memuatkan senarai inisiatif...</p>
    </div>

    <!-- Project Table -->
    <div v-else-if="filteredProjects.length > 0" class="table-container">
      <b-table :items="paginatedProjects" :fields="visibleFields" responsive hover striped bordered
        class="project-table rounded-lg shadow-sm" style="table-layout: fixed; width: 100%;">
        <!-- Nama Inisiatif (truncate) -->
        <template #cell(project_name)="data">
          <div class="text-dark text-truncate" :class="{ 'truncate-nonadmin': user.peranan !== 'admin' }"
            :style="user.peranan !== 'admin' ? 'max-width: 260px;' : 'max-width: 160px;'"
            :title="data.item.project_name">
            {{ data.item.project_name }}
          </div>
        </template>
        <!-- Nama Pentadbir -->
        <template #cell(nama_pentadbir)="data">
          <div class="text-dark text-truncate" :class="{ 'truncate-nonadmin': user.peranan !== 'admin' }"
            :style="user.peranan !== 'admin' ? 'max-width: 220px;' : 'max-width: 160px;'"
            :title="data.item.nama_pentadbir">
            {{ data.item.nama_pentadbir }}
          </div>
        </template>
        <!-- Progress -->
        <template #cell(progress)="data">
          <div class="text-dark text-center">
            {{ data.item.progress || 'N/A' }}%
          </div>
        </template>
        <template #cell(countdown)="data" v-if="user.peranan === 'admin'">
          <div v-if="data.item.status !== 'DISAHKAN' && data.item.status !== 'TIDAK DISAHKAN'"
            class="text-dark text-center countdown-cell">
            <template v-if="isPenilaiMatch(data.item)">
              <span :class="{
                'countdown-active': countdowns[data.item.project_id] && !countdowns[data.item.project_id].includes('-') && !countdowns[data.item.project_id].includes('Akan Berakhir'),
                'countdown-pending': countdowns[data.item.project_id] && countdowns[data.item.project_id].includes('Akan Berakhir'),
                'countdown-available': countdowns[data.item.project_id] === '-'
              }">
                {{ countdowns[data.item.project_id] || calculateCountdown(data.item.check_in_penilai, data.item.status)
                }}
              </span>
            </template>
            <template v-else-if="isInEvaluationPeriod(data.item)">
              <span class="text-primary">Sedang Dinilai</span>
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </div>
          <div v-else class="text-muted text-center">-</div>
        </template>
        <template #cell(status)="data">
          <div class="d-flex justify-content-center align-items-center gap-1">
            <!-- BAHARU -->
            <b-button v-if="data.item.status === 'BARU'" variant="outline-primary" size="sm"
              class="status-baru-btn rounded-pill" @click="showStatusChangeModal(data.item)"
              :disabled="isActionButtonDisabled(data.item)">
              <i class="fas fa-paper-plane mr-1"></i> BAHARU
            </b-button>

            <!-- STATUS LAIN -->
            <b-badge v-else :variant="getStatusVariant(data.item.status)" class="status-badge rounded-pill"
              :class="{ 'badge-small-text': data.item.status === 'TIDAK DISAHKAN' }">
              <i :class="getStatusIcon(data.item.status)" class="mr-1"></i>
              {{ getDisplayStatus(data.item.status) }}
            </b-badge>
            <!-- <b-button v-if="data.item.status === 'TIDAK DISAHKAN' && data.item.ulasan && user.peranan !== 'admin'"
              variant="outline-danger" size="xs" class="small-review-btn" @click="showReview(data.item.ulasan)">
              <i class="fas fa-comment"></i>
            </b-button> -->
          </div>
        </template>
        <template #cell(certificate)="data">
          <b-button v-if="data.item.status === 'DISAHKAN'" variant="outline-success" size="sm"
            class="rounded-pill px-2 py-1 btn-xs-custom" @click="printCertificate(data.item.project_id)">
            <i class="fas fa-print mr-1"></i> Cetak Sijil
          </b-button>
        </template>
        <template #cell(details)="data">
          <b-button v-if="user.peranan === 'admin'" variant="outline-info" size="sm"
            class="rounded-pill px-2 py-1 btn-xs-custom" @click="viewProjectDetails(data.item.project_id)">
            <i class="fas fa-file-pdf mr-1"></i> Butiran
          </b-button>
        </template>
        <template #cell(actions)="data">
          <b-button variant="outline-primary" size="sm" class="rounded-pill px-2 py-1 btn-xs-custom"
            @click="handleActionClick(data.item)" :disabled="isActionButtonDisabled(data.item)">
            <i class="fas fa-eye mr-1"></i>
            {{ user.peranan === 'admin' ? 'Tindakan' : 'Papar' }}
          </b-button>
        </template>
        <template #cell(download)="data">
          <b-button v-if="user.peranan === 'admin'" variant="outline-success" size="sm" class="rounded-pill"
            @click="downloadProject(data.item.project_id)">
            <i class="fas fa-download mr-1"></i> Muat Turun
          </b-button>
        </template>
        <!-- Nilai Inisiatif with Color Coding -->
        <template #cell(rating_category)="data">
          <div class="text-center font-weight-bold" :class="getRatingCategoryClass(data.item.rating_category)">
            {{ data.item.rating_category || 'Belum Dinilai' }}
          </div>
        </template>
      </b-table>
      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div class="text-muted small mr-3">
          <strong>Papar {{ paginationStart }} - {{ paginationEnd }} daripada {{ filteredProjects.length }}
            inisiatif</strong>
        </div>
        <div>
          <b-pagination v-model="currentPage" :total-rows="filteredProjects.length" :per-page="perPage" pills
            @change="handlePageChange"></b-pagination>
        </div>
      </div>
    </div>
    <!-- No Projects -->
    <div v-else class="no-data-container text-center py-5">
      <i class="fas fa-folder-open fa-3x text-muted mb-3"></i>
      <h4 class="text-dark font-weight-bold">Tiada Inisiatif</h4>
      <p v-if="user && user.peranan && user.peranan !== 'admin'" class="text-muted mb-4">Sila tambah initiatif baharu
        untuk
        memulakan.</p>
      <b-button v-if="user && user.peranan && user.peranan !== 'admin'" variant="primary" class="px-4 py-2 rounded-pill"
        @click="$router.push('/projek/add')">
        <i class="fas fa-plus mr-2"></i> Tambah Inisiatif
      </b-button>
    </div>
    <!-- Modal Ulasan -->
    <!-- <b-modal id="reviewModal" title="Ulasan Inisiatif" centered hide-footer>
      <p class="text-dark" v-if="selectedReview">{{ selectedReview }}</p>
      <p v-else class="text-muted">Tiada ulasan tersedia.</p>
    </b-modal> -->
    <!-- Modal Tukar Status -->
    <b-modal id="statusChangeModal" title="Tukar Status Inisiatif" centered hide-footer>
      <div class="text-center py-3">
        <i class="fas fa-exchange-alt fa-3x text-warning mb-3"></i>
        <h5 class="mb-3">Adakah anda pasti ingin menukar status inisiatif ini kepada DRAF?</h5>
        <p class="text-muted">Status inisiatif akan ditukar daripada <strong>BAHARU</strong> kepada
          <strong>DRAF</strong>.
        </p>
      </div>
      <div class="d-flex justify-content-center gap-3 mt-4">
        <b-button variant="outline-secondary" class="px-4 rounded-pill" @click="$bvModal.hide('statusChangeModal')">
          <i class="fas fa-times mr-2"></i>Tidak
        </b-button>
        <b-button variant="primary" class="px-4 rounded-pill" @click="confirmStatusChange" :disabled="isChangingStatus">
          <b-spinner small v-if="isChangingStatus" class="mr-2"></b-spinner>
          <i v-else class="fas fa-check mr-2"></i>
          {{ isChangingStatus ? 'Memproses...' : 'Ya' }}
        </b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import axios from 'axios';
import { baseURL } from '../../../services/baseURL';
import Swal from 'sweetalert2';
import {
  BIconCalendar,
  BIconChevronLeft,
  BIconChevronRight,
  BIconX,
  BIconCheck
} from 'bootstrap-vue';

export default {
  name: 'ProjectList',
  components: {
    BIconCalendar,
    BIconChevronLeft,
    BIconChevronRight,
    BIconX,
    BIconCheck
  },
  data() {
    return {
      selectedProjectForStatusChange: null,
      isChangingStatus: false,
      agencyType: null, // 'kerajaan', 'swasta', atau null untuk semua
      agencyTypeOptions: [
        { value: null, text: 'Semua Jenis Organisasi' },
        { value: 'kerajaan', text: 'Agensi HRMIS ' },
        { value: 'swasta', text: 'Agensi Bukan HRMIS ' }
      ],
      privateAgencies: [],
      pageOptions: [
        { value: 5, text: '5' },
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
        { value: 100, text: '100' }
      ],
      showMonthYearPicker: false,
      selectedMonth: null,
      selectedYear: new Date().getFullYear(),
      tempSelectedMonth: null,
      tempSelectedYear: new Date().getFullYear(),
      months: ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'],
      selectedReview: null,
      selectedAgency: null,
      agencyOptions: [{ value: null, text: 'Semua Agensi' }],
      projects: [],
      filteredProjects: [],
      currentPage: 1,
      perPage: 10,
      user: JSON.parse(localStorage.getItem('user')) || {},
      selectedMinistry: null,
      ministryOptions: [
        { value: null, text: 'Semua Organisasi' },
        { value: '1', text: 'Organisasi A' },
        { value: '2', text: 'Organisasi B' },
      ],
      selectedStatus: null,
      statusOptions: [
        { value: null, text: 'Semua Status' },
        { value: 'DRAF', text: 'DRAF' },
        { value: 'BARU', text: 'BARU' },
        { value: 'DISAHKAN', text: 'DISAHKAN' },
        { value: 'TIDAK DISAHKAN', text: 'TIDAK DISAHKAN' },
      ],
      projectNameFilter: '',
      isLoading: false,
      countdowns: {}, // Simpan kiraan masa untuk setiap projek
      timer: null, // Rujukan kepada setInterval
    };
  },
  computed: {
    isAgencyFilterDisabled() {
      if (this.user.peranan === 'pentadbir') {
        return false; // Pentadbir selalu enabled
      }
      if (this.user.peranan === 'admin') {
        return !this.agencyType || (this.agencyType === 'kerajaan' && !this.selectedMinistry);
      }
      return true;
    },
    paginationStart() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      const end = this.currentPage * this.perPage;
      return end > this.filteredProjects.length ? this.filteredProjects.length : end;
    },
    monthYearDisplay() {
      if (this.selectedMonth && this.selectedYear) {
        return `${this.months[this.selectedMonth - 1]} ${this.selectedYear}`;
      }
      return '';
    },
    filteredStatusOptions() {
      if (this.user && this.user.peranan === 'admin') {
        return [
          { value: null, text: 'Semua Status' },
          { value: 'BARU', text: 'BAHARU' },
          { value: 'DISAHKAN', text: 'DISAHKAN' },
          { value: 'TIDAK DISAHKAN', text: 'TIDAK DISAHKAN' },
        ];
      }
      return [
        { value: null, text: 'Semua Status' },
        { value: 'DRAF', text: 'DRAF' },
        { value: 'BARU', text: 'BAHARU' },
        { value: 'DISAHKAN', text: 'DISAHKAN' },
        { value: 'TIDAK DISAHKAN', text: 'TIDAK DISAHKAN' },
      ];
    },
    availableAgencies() {
      if (this.user.peranan === 'pentadbir') {
        return this.agencyOptions;
      }
      if (this.user.peranan === 'admin') {
        if (this.agencyType === 'kerajaan') {
          if (!this.selectedMinistry) {
            return [{ value: null, text: 'Sila pilih organisasi dahulu' }];
          }
          return this.agencyOptions;
        } else if (this.agencyType === 'swasta') {
          if (this.privateAgencies.length > 0) {
            return this.privateAgencies;
          } else {
            return [{ value: null, text: 'Memuat Organisasi...' }];
          }
        } else {
          if (!this.agencyType) {
            return [{ value: null, text: 'Sila pilih jenis organisasi dahulu' }];
          }
          const allAgencies = [...this.agencyOptions];
          if (this.privateAgencies.length > 0) {
            const privateWithoutAll = this.privateAgencies.filter(agency => agency.value !== null);
            allAgencies.push(...privateWithoutAll);
          }
          return allAgencies;
        }
      }
      return [];
    },
    paginatedProjects() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredProjects.slice(start, end);
    },
    visibleFields() {
      const fields = this.getFields().filter(field => !field.adminOnly || this.user.peranan === 'admin');
      if (this.user.peranan !== 'admin') {
        return fields.filter(field => field.key !== 'countdown');
      }
      return fields;
    },
  },
  methods: {
    showStatusChangeModal(project) {
      // Hanya benarkan jika status BAHARU dan tiada penilaian bermula
      if (project.status !== 'BARU' || project.check_in_penilai || project.id_penilai) {
        this.$bvToast.toast('Tidak boleh menukar status. Penilaian mungkin sudah bermula.', {
          title: 'Amaran',
          variant: 'warning',
          solid: true,
        });
        return;
      }

      this.selectedProjectForStatusChange = project;
      this.$bvModal.show('statusChangeModal');
    },


    async confirmStatusChange() {
      if (!this.selectedProjectForStatusChange) return;
      this.isChangingStatus = true;
      const project = this.selectedProjectForStatusChange;
      const userId = this.user.id || this.user.icno || this.user.icNumber;
      const userName = this.user.name || this.user.username || 'Pentadbir';
      const projectName = project.project_name || 'Inisiatif';

      try {
        const response = await axios.post(`${baseURL}/revert_to_draft.php`, {
          project_id: project.project_id,
          user_id: userId,
          user_name: userName
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.data.success) {
          // Kemas kini senarai projek
          const index = this.projects.findIndex(p => p.project_id === project.project_id);
          if (index !== -1) {
            this.projects[index].status = 'DRAF';
          }
          this.filteredProjects = this.filteredProjects.filter(p => p.project_id !== project.project_id);

          // Betulkan pagination
          if (this.filteredProjects.length === 0 && this.currentPage > 1) {
            this.currentPage = 1;
          }

          // Tutup modal
          this.$bvModal.hide('statusChangeModal');
          this.selectedProjectForStatusChange = null;

          // Tunggu seketika sebelum refresh
          await new Promise(resolve => setTimeout(resolve, 800));
          await this.getProjects();

          // === SWEETALERT2: Tambah nama inisiatif ===
          await Swal.fire({
            title: 'Berjaya!',
            html: `
          Status inisiatif <strong>"${projectName}"</strong>
          berjaya ditukar kepada <strong>DRAF</strong>.
        `,
            icon: 'success',
            confirmButtonText: 'OK',
            customClass: {
              confirmButton: 'btn btn-success mx-2',
              popup: 'swal2-popup-custom',
              title: 'swal2-title-custom',
              content: 'swal2-content-custom'
            },
            buttonsStyling: false
          });
        } else {
          throw new Error(response.data.message || 'Gagal menukar status');
        }
      } catch (error) {
        // console.error('Error reverting to draft:', error);
        await Swal.fire({
          title: 'Ralat!',
          text: (error.response && error.response.data && error.response.data.message)
            ? error.response.data.message
            : 'Ralat sistem ketika menukar status inisiatif',
          icon: 'error',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'btn btn-danger mx-2'
          },
          buttonsStyling: false
        });
      } finally {
        this.isChangingStatus = false;
      }
    },

    async fetchPrivateAgencies() {
      try {
        const response = await axios.get(`${baseURL}/get_agencies_private.php`);
        if (response.data.status === 'success') {
          this.privateAgencies = [
            { value: null, text: 'Semua Bukan HRMIS' },
            ...response.data.data.map(agency => ({
              value: agency.buorgchart || agency.buid_kementerian || agency.nama_kementerian,
              text: agency.nama_kementerian,
            }))
          ];
        }
      } catch (error) {
        this.$bvToast.toast('Gagal memuat senarai organisasi', {
          title: 'Ralat',
          variant: 'danger',
          solid: true,
        });
      }
    },
    async handleAgencyTypeChange() {
      this.selectedMinistry = null;
      this.selectedAgency = null; // Reset pilihan agensi
      if (this.user.peranan === 'admin') {
        this.currentPage = 1; // Reset to default page for admin when agency type changes
      }
      if (this.agencyType === 'swasta') {
        await this.fetchPrivateAgencies();
      } else if (this.agencyType === 'kerajaan' && this.user.peranan === 'admin') {
        await this.fetchMinistries();
      }
      this.filterProjects();
    },
    isActionButtonDisabled(project) {
      // 1. Bukan admin: sentiasa benarkan (seperti asal)
      if (this.user.peranan !== 'admin') {
        return false;
      }

      // 2. Admin: Jika status DISAHKAN atau TIDAK DISAHKAN → benarkan
      if (project.status === 'DISAHKAN' || project.status === 'TIDAK DISAHKAN') {
        return false;
      }

      // 3. Admin: Jika status BARU dan tiada penilaian langsung → benarkan tukar ke DRAF
      if (project.status === 'BARU' && !project.check_in_penilai && !project.id_penilai) {
        return false;
      }

      // 4. Admin: Jika penilai MATCH (id_penilai === user.id) → BENARKAN tanpa mengira masa
      const currentUserId = this.user.id || this.user.icno || this.user.icNumber;
      if (project.id_penilai && project.id_penilai == currentUserId) {
        return false; // ← INI YANG PALING PENTING
      }

      // 5. Admin: Jika sedang dalam tempoh penilaian (24 jam) dan penilai lain → disable
      if (this.isInEvaluationPeriod(project)) {
        return true;
      }

      // 6. Selain daripada itu (contoh: masa sudah tamat & tiada penilai) → benarkan
      return false;
    },
    async handleActionClick(project) {
      if (this.isActionButtonDisabled(project)) {
        return;
      }
      if (
        this.user.peranan !== 'admin' ||
        project.status !== 'BARU' ||
        this.isInEvaluationPeriod(project)
      ) {
        this.$router.push(`/projek/view/${project.project_id}`);
        return;
      }
      try {
        const checkResponse = await axios.get(`${baseURL}/check_penilai_status.php`, {
          params: { project_id: project.project_id }
        });
        if (checkResponse.data.status === 'success' && checkResponse.data.check_in_penilai) {
          const checkIn = new Date(checkResponse.data.check_in_penilai + '+08:00');
          const deadline = new Date(checkIn);
          deadline.setHours(deadline.getHours() + 24);
          const now = new Date();
          if (now > deadline) {
            const result = await Swal.fire({
              title: 'Adakah anda ingin menilai inisiatif ini?',
              text: 'Inisiatif ini berstatus BAHARU. Sila klik "Ya" untuk ke paparan penilaian.',
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Ya',
              cancelButtonText: 'Tidak',
              reverseButtons: true,
              customClass: {
                confirmButton: 'btn btn-primary mx-2',
                cancelButton: 'btn btn-outline-secondary mx-2'
              },
              buttonsStyling: false
            });
            if (result.isConfirmed) {
              const secondCheckResponse = await axios.get(`${baseURL}/check_penilai_status.php`, {
                params: { project_id: project.project_id }
              });
              if (secondCheckResponse.data.status === 'success' && secondCheckResponse.data.check_in_penilai) {
                const secondCheckIn = new Date(secondCheckResponse.data.check_in_penilai + '+08:00');
                const secondDeadline = new Date(secondCheckIn);
                secondDeadline.setHours(secondDeadline.getHours() + 24);
                if (now > secondDeadline) {
                  const updateResponse = await axios.post(`${baseURL}/update_rating_timestamp.php`, {
                    project_id: project.project_id,
                    penilai_id: this.user.id
                  });
                  if (updateResponse.data.status === 'success') {
                    this.$router.push(`/projek/view/${project.project_id}`);
                  } else {
                    this.showErrorToast('Gagal mengemas kini rekod penilaian.');
                  }
                } else {
                  await Swal.fire({
                    title: 'Insiatif sedang dinilai',
                    text: 'Penilaian inisiatif ini sedang dijalankan oleh penilai lain',
                    icon: 'warning',
                    confirmButtonText: 'OK',
                    customClass: { confirmButton: 'btn btn-primary mx-2' },
                    buttonsStyling: false
                  });
                  window.location.reload();
                  return;
                }
              } else {
                const updateResponse = await axios.post(`${baseURL}/update_rating_timestamp.php`, {
                  project_id: project.project_id,
                  penilai_id: this.user.id
                });
                if (updateResponse.data.status === 'success') {
                  this.$router.push(`/projek/view/${project.project_id}`);
                } else {
                  this.showErrorToast('Gagal mengemas kini rekod penilaian.');
                }
              }
            }
            return;
          } else {
            await Swal.fire({
              title: 'Insiatif sedang dinilai',
              text: 'Penilaian inisiatif ini sedang dijalankan oleh penilai lain',
              icon: 'warning',
              confirmButtonText: 'OK',
              customClass: { confirmButton: 'btn btn-primary mx-2' },
              buttonsStyling: false
            });
            window.location.reload();
            return;
          }
        } else {
          const result = await Swal.fire({
            title: 'Adakah anda ingin menilai inisiatif ini?',
            text: 'Inisiatif ini berstatus BAHARU. Sila klik "Ya" untuk ke paparan penilaian.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Ya',
            cancelButtonText: 'Tidak',
            reverseButtons: true,
            customClass: {
              confirmButton: 'btn btn-primary mx-2',
              cancelButton: 'btn btn-outline-secondary mx-2'
            },
            buttonsStyling: false
          });
          if (result.isConfirmed) {
            const secondCheckResponse = await axios.get(`${baseURL}/check_penilai_status.php`, {
              params: { project_id: project.project_id }
            });
            if (secondCheckResponse.data.status === 'success' && secondCheckResponse.data.check_in_penilai) {
              const secondCheckIn = new Date(secondCheckResponse.data.check_in_penilai + '+08:00');
              const secondDeadline = new Date(secondCheckIn);
              secondDeadline.setHours(secondDeadline.getHours() + 24);
              if (now > secondDeadline) {
                const updateResponse = await axios.post(`${baseURL}/update_rating_timestamp.php`, {
                  project_id: project.project_id,
                  penilai_id: this.user.id
                });
                if (updateResponse.data.status === 'success') {
                  this.$router.push(`/projek/view/${project.project_id}`);
                } else {
                  this.showErrorToast('Gagal mengemas kini rekod penilaian.');
                }
              } else {
                await Swal.fire({
                  title: 'Insiatif sedang dinilai',
                  text: 'Penilaian inisiatif ini sedang dijalankan oleh penilai lain',
                  icon: 'warning',
                  confirmButtonText: 'OK',
                  customClass: { confirmButton: 'btn btn-primary mx-2' },
                  buttonsStyling: false
                });
                window.location.reload();
                return;
              }
            } else {
              const updateResponse = await axios.post(`${baseURL}/update_rating_timestamp.php`, {
                project_id: project.project_id,
                penilai_id: this.user.id
              });
              if (updateResponse.data.status === 'success') {
                this.$router.push(`/projek/view/${project.project_id}`);
              } else {
                this.showErrorToast('Gagal mengemas kini rekod penilaian.');
              }
            }
          }
          return;
        }
      } catch (error) {
        this.showErrorToast('Ralat sistem ketika memproses permintaan.');
      }
    },
    getDisplayStatus(status) {
      const statusMap = {
        'BARU': 'BAHARU',
      };
      return statusMap[status] || status;
    },
    getFields() {
      const isAdmin = this.user.peranan === 'admin';
      return [
        {
          key: 'project_name',
          label: 'Nama Inisiatif',
          sortable: true,
          class: 'align-middle',
          thStyle: { width: isAdmin ? '160px' : '260px' },
          tdClass: 'text-truncate',
        },
        {
          key: 'nama_pentadbir',
          label: 'Nama Pentadbir',
          sortable: true,
          class: 'align-middle',
          thStyle: { width: isAdmin ? '150px' : '220px' },
          tdClass: 'text-truncate',
        },
        {
          key: 'status',
          label: isAdmin ? 'Status Penilaian' : 'Status',
          sortable: true,
          class: 'align-middle text-center',
          thStyle: { width: '200px' }, // ← LEBAR BARU
        },
        {
          key: 'countdown',
          label: 'Masa Penilaian',
          sortable: false,
          class: 'align-middle text-center',
          thStyle: { width: '150px' },
          adminOnly: true,
        },
        ...(isAdmin
          ? [
            {
              key: 'details',
              label: 'Keterangan Inisiatif',
              class: 'align-middle text-center',
              thStyle: { width: '160px' },
              adminOnly: true,
            },
          ]
          : []),
        {
          key: 'actions',
          label: isAdmin ? 'Penilaian Inisiatif' : 'Tindakan',
          class: 'align-middle text-center',
          thStyle: { width: isAdmin ? '150px' : '180px' },
        },
        ...(isAdmin
          ? [
            {
              key: 'rating_category',
              label: 'Nilai Inisiatif',
              sortable: true,
              class: 'align-middle text-center',
              thStyle: { width: '130px' },
              adminOnly: true,
            },
            {
              key: 'certificate',
              label: 'Cetakan Sijil',
              class: 'align-middle text-center',
              thStyle: { width: '140px' },
              adminOnly: true,
            },
          ]
          : []),
      ];
    },
    isMonthSelected(month) {
      return this.tempSelectedMonth === month && this.tempSelectedYear === this.tempSelectedYear;
    },
    prevYear() {
      this.tempSelectedYear--;
    },
    nextYear() {
      this.tempSelectedYear++;
    },
    selectMonth(month) {
      this.tempSelectedMonth = month;
      this.selectedMonth = month;
      this.selectedYear = this.tempSelectedYear;
      this.showMonthYearPicker = false;
      this.filterProjects();
    },
    clearMonthYearFilter() {
      this.tempSelectedMonth = null;
      this.tempSelectedYear = new Date().getFullYear();
      this.selectedMonth = null;
      this.selectedYear = new Date().getFullYear();
      this.showMonthYearPicker = false;
      this.filterProjects();
    },
    getRatingCategoryClass(category) {
      switch (category) {
        case 'Cemerlang':
          return 'text-success';
        case 'Baik':
          return 'text-primary';
        case 'Sederhana':
          return 'text-warning';
        case 'Lemah':
          return 'text-orange';
        case 'Perlu Penambahbaikan':
        case 'Memuaskan':
          return 'text-danger';
        default:
          return 'text-muted';
      }
    },
    showReview(ulasan) {
      this.selectedReview = ulasan;
      this.$bvModal.show('reviewModal');
    },
    async fetchMinistries() {
      try {
        const response = await axios.get(`${baseURL}/get_ministries.php`);
        if (response.data.status === 'success') {
          this.ministryOptions = [
            { value: null, text: 'Semua Organisasi' },
            ...response.data.data.map(ministry => ({
              value: ministry.buid_kementerian,
              text: ministry.nama_kementerian,
            })),
          ];
        } else {
          this.$bvToast.toast('Gagal memuat senarai organisasi.', {
            title: 'Ralat',
            variant: 'danger',
            solid: true,
          });
        }
      } catch (error) {
        this.$bvToast.toast('Tidak dapat menghubungi pelayan untuk mendapatkan senarai organisasi.', {
          title: 'Ralat',
          variant: 'danger',
          solid: true,
        });
      }
    },
    async fetchAgencies(buorgchart) {
      try {
        const endpoint = this.user.peranan === 'pentadbir'
          ? `${baseURL}/get_agencies_buid.php`
          : `${baseURL}/get_agencies.php`;
        const params = this.user.peranan === 'pentadbir'
          ? { buid_kementerian: this.user.buidagensi }
          : { buid_kementerian: buorgchart };
        const response = await axios.get(endpoint, { params });
        if (response.data.status === 'success') {
          this.agencyOptions = [
            { value: null, text: 'Semua Agensi' },
            ...response.data.data.map(agency => ({
              value: agency.buid_agensi || agency.nama_agensi,
              text: agency.nama_agensi || agency.buid_agensi,
            })),
          ];
        }
      } catch (error) {
        this.$bvToast.toast('Gagal memuat senarai agensi', {
          title: 'Ralat',
          variant: 'danger',
          solid: true,
        });
      }
    },
    async handleMinistryChange() {
      this.selectedAgency = null;
      if (this.user.peranan === 'admin' && this.selectedMinistry && this.agencyType !== 'swasta') {
        await this.fetchAgencies(this.selectedMinistry);
      } else if (this.user.peranan === 'pentadbir') {
        await this.fetchAgencies(this.user.buidagensi);
      }
      this.filterProjects();
    },
    handlePageChange(page) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    navigateToProject(projectId) {
      this.$router.push(`/projek/view/${projectId}`);
    },
    printCertificate(projectId) {
      window.open(`${baseURL}/pdf/projek/sijil_projek.php?project_id=${projectId}`, '_blank');
    },
    viewProjectDetails(projectId) {
      window.open(`${baseURL}/pdf/projek/detail_projek.php?project_id=${projectId}`, '_blank');
    },
    async downloadProject(projectId) {
      try {
        const response = await axios.get(`${baseURL}/download_project.php?project_id=${projectId}`, {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `project_${projectId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$bvToast.toast('Gagal memuat turun inisiatif.', {
          title: 'Ralat',
          variant: 'danger',
          solid: true,
        });
      }
    },
    async getProjects() {
      this.isLoading = true;
      try {
        const user = JSON.parse(localStorage.getItem('user')) || {};
        if (!user.peranan) {
          this.projects = [];
          this.filteredProjects = [];
          return;
        }
        const requestData = {
          request: {
            peranan: user.peranan,
            buidagensi: user.buidagensi,
            icno: user.icno,
            icNumber: user.icNumber,
          },
        };
        const response = await axios.post(`${baseURL}/papar_projek.php`, requestData);
        this.projects = Array.isArray(response.data) ? response.data.map(project => ({
          ...project,
          ulasan: project.status === 'TIDAK DISAHKAN' ? (project.comment || 'Tiada ulasan') : '',
          nama_pentadbir: project.name,
          progress: project.progress || '0',
          rating_category: project.rating_category || 'Belum Dinilai',
        })) : [];
        this.filteredProjects = [...this.projects];
        this.startCountdown();
      } catch (error) {
        this.projects = [];
        this.filteredProjects = [];
      } finally {
        this.isLoading = false;
      }
    },
    filterProjects() {
      let filtered = [...this.projects];

      // === 1. SENTIASA TAPIS DRAF (untuk semua peranan) ===
      filtered = filtered.filter(project => project.status !== 'DRAF');
      // === 2. Bulan/Tahun ===
      if (this.selectedMonth && this.selectedYear) {
        filtered = filtered.filter(project => {
          if (!project.updated_at) return false;
          const date = new Date(project.updated_at);
          if (isNaN(date.getTime())) return false;
          return (
            date.getMonth() + 1 === this.selectedMonth &&
            date.getFullYear() === this.selectedYear
          );
        });
      }

      // === 3. Jenis Agensi (admin sahaja) ===
      if (this.agencyType === 'kerajaan') {
        filtered = filtered.filter(project => project.user_type === 'kerajaan');
      } else if (this.agencyType === 'swasta') {
        filtered = filtered.filter(project => project.user_type === 'awam');
      }

      // === 4. Kementerian (admin + kerajaan) ===
      if (this.selectedMinistry && this.user.peranan === 'admin' && this.agencyType !== 'swasta') {
        filtered = filtered.filter(project => {
          const projectMinistry = project.ministry || '';
          const selectedMinistryOption = this.ministryOptions.find(
            m => m.value === this.selectedMinistry
          );
          const selectedMinistryText = selectedMinistryOption ? selectedMinistryOption.text : '';
          return (
            projectMinistry.trim().toUpperCase() === selectedMinistryText.trim().toUpperCase() ||
            project.kod_ministry === this.selectedMinistry
          );
        });
      }

      // === 5. Agensi ===
      if (this.selectedAgency) {
        filtered = filtered.filter(project => {
          const projectAgency = project.agency || '';
          const projectDepartment = project.department || '';
          const allAgencies = [...this.agencyOptions, ...this.privateAgencies];
          const selectedAgencyOption = allAgencies.find(
            a => a.value === this.selectedAgency
          );
          if (!selectedAgencyOption) return false;
          const selectedAgencyText = selectedAgencyOption.text || '';
          return (
            projectAgency.trim().toUpperCase() === selectedAgencyText.trim().toUpperCase() ||
            projectDepartment.toString() === this.selectedAgency.toString() ||
            project.ministry === selectedAgencyText
          );
        });
      }

      // === 6. Status Penilaian ===
      if (this.selectedStatus) {
        filtered = filtered.filter(project =>
          (project.status || '').toString().trim().toUpperCase() ===
          this.selectedStatus.toString().trim().toUpperCase()
        );
      }

      // === 7. Nama Inisiatif ===
      if (this.projectNameFilter) {
        filtered = filtered.filter(project =>
          (project.project_name || '')
            .toString()
            .trim()
            .toUpperCase()
            .includes(this.projectNameFilter.trim().toUpperCase())
        );
      }

      this.filteredProjects = filtered;
      this.currentPage = 1;
    },
    clearProjectNameFilter() {
      this.projectNameFilter = '';
      this.filterProjects();
    },
    getStatusVariant(status) {
      switch (status) {
        case 'DRAF':
          return 'warning';
        case 'BARU':
          return 'primary';
        case 'DISAHKAN':
          return 'success';
        case 'TIDAK DISAHKAN':
          return 'danger';
        default:
          return 'secondary';
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case 'DRAF':
          return 'fas fa-file-alt';
        case 'BARU':
          return 'fas fa-paper-plane';
        case 'DISAHKAN':
          return 'fas fa-check-circle';
        case 'TIDAK DISAHKAN':
          return 'fas fa-times-circle';
        default:
          return 'fas fa-question-circle';
      }
    },
    rowClass(item) {
      if (!item) return '';
      return `status-${item.status.toLowerCase().replace(' ', '-')}`;
    },
    isPenilaiMatch(project) {
      return project.id_penilai === this.user.id;
    },
    isInEvaluationPeriod(project) {
      if (!project.check_in_penilai || project.status === 'DISAHKAN' || project.status === 'TIDAK DISAHKAN') {
        return false;
      }
      const checkIn = new Date(project.check_in_penilai + '+08:00');
      const deadline = new Date(checkIn);
      deadline.setHours(deadline.getHours() + 24);
      const now = new Date();
      return now >= checkIn && now <= deadline;
    },
    calculateCountdown(checkInDate, status) {
      if (!checkInDate || status === 'DISAHKAN' || status === 'TIDAK DISAHKAN') {
        return null;
      }
      const checkIn = new Date(checkInDate + '+08:00');
      const deadline = new Date(checkIn);
      deadline.setHours(deadline.getHours() + 24);
      const now = new Date();
      if (now < checkIn) {
        const timeToStart = checkIn - now;
        const hours = Math.floor((timeToStart % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeToStart % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeToStart % (1000 * 60)) / 1000);
        return `Akan Berakhir: ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
      const timeDiff = deadline - now;
      if (timeDiff <= 0) {
        return '-';
      }
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    startCountdown() {
      this.stopCountdown();
      this.timer = setInterval(() => {
        this.paginatedProjects.forEach(project => {
          if (project.check_in_penilai &&
            project.status !== 'DISAHKAN' &&
            project.status !== 'TIDAK DISAHKAN' &&
            this.isPenilaiMatch(project)) {
            this.$set(this.countdowns, project.project_id, this.calculateCountdown(project.check_in_penilai, project.status));
          }
        });
      }, 1000);
    },
    stopCountdown() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Ralat',
        variant: 'danger',
        solid: true,
      });
    },
  },
  async created() {
    if (this.user.peranan === 'admin') {
      await this.fetchMinistries();
      await this.fetchPrivateAgencies();
    } else if (this.user.peranan === 'pentadbir') {
      await this.fetchAgencies(this.user.buidagensi);
    }
    await this.getProjects();
    this.tempSelectedYear = new Date().getFullYear();
    this.selectedYear = new Date().getFullYear();
  },
  beforeDestroy() {
    this.stopCountdown();
  },
};
</script>

<style scoped>
/* Existing styles remain unchanged */
.table-container {
  margin-top: 1.5rem;
}

.project-table {
  background: #fff;
  border: 1px solid #e9ecef;
}

.project-table thead th {
  background: #f8f9fa;
  color: #343a40;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  border-bottom: 2px solid #dee2e6;
  padding: 1rem;
}

.project-table tbody td {
  vertical-align: middle;
  font-size: 0.875rem;
  padding: 0.75rem;
}

.project-table tbody tr:hover {
  background: #f1f3f5;
}

.status-draf {
  border-left: 4px solid #ffc107;
}

.status-baru {
  border-left: 4px solid #007bff;
}

.status-disahkan {
  border-left: 4px solid #28a745;
}

.status-tidak-disahkan {
  border-left: 4px solid #dc3545;
}

.custom-select,
.form-control {
  border: 1px solid #ced4da;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.custom-select:focus,
.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.1);
}

.filter-boom {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.filter-boom:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15);
}

.filter-boom:focus {
  transform: scale(1.02);
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.clear-btn {
  padding: 0.5rem;
  transition: transform 0.2s ease;
}

.clear-btn:hover {
  transform: scale(1.1);
}

.clear-btn .fas {
  font-size: 1rem;
}

.loading-container {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.no-data-container {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.text-dark {
  color: #343a40 !important;
}

.text-muted {
  color: #6c757d !important;
}

.btn-outline-primary,
.btn-outline-success,
.btn-outline-info {
  font-size: 0.875rem;
  padding: 0.4rem 1rem;
  border-width: 1px;
}

.btn-primary {
  font-size: 0.875rem;
  padding: 0.5rem 1.5rem;
}

.rounded-pill {
  border-radius: 50rem !important;
}

h3 {
  font-size: 1.5rem;
}

h4 {
  font-size: 1.25rem;
}

p {
  font-size: 0.875rem;
}

/* .badge {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
} */

/* .small-review-btn {
  padding: 0.2rem 0.4rem;
  font-size: 0.65rem;
  line-height: 1;
} */

.small-review-btn .fas {
  font-size: 0.65rem;
}

.project-table td {
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* .btn-xs-custom {
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
} */

/* .badge-small-text {
  font-size: 0.7rem;
} */

.truncate-cell {
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

.truncate-nonadmin {
  max-width: 260px;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-success {
  color: #28a745 !important;
}

.text-primary {
  color: #007bff !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-orange {
  color: #fd7e14 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d !important;
}

.calendar-icon {
  position: absolute;
  right: 15px;
  top: 10px;
  color: #6c757d;
  cursor: pointer;
}

.month-year-picker {
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-width: 280px;
  margin-top: 5px;
  border-radius: 0.5rem;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px;
}

.month-button {
  width: 100%;
  padding: 0.25rem;
  font-size: 0.8rem;
}

.position-relative {
  position: relative;
}

.month-year-picker {
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-width: 280px;
  margin-top: 5px;
  border-radius: 0.5rem;
  padding: 1rem;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.month-button {
  width: 100%;
  padding: 0.25rem;
  font-size: 0.8rem;
  transition: all 0.2s ease;
}

.month-button:hover {
  transform: scale(1.05);
}

.filter-boom {
  background-color: white !important;
}

.custom-select {
  background-color: white !important;
}

.month-year-filter-container {
  width: 100%;
}

.filter-boom,
.custom-select {
  width: 100%;
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
}

.calendar-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  cursor: pointer;
  pointer-events: none;
}

.position-relative {
  position: relative;
}

.month-year-picker {
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-width: 280px;
  margin-top: 5px;
  border-radius: 0.5rem;
}

.records-selector {
  width: 80px;
  display: inline-block;
  background-color: white !important;
  border: 1px solid #ced4da;
  transition: all 0.3s ease;
}

.records-selector:hover {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.1);
}

.records-selector:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.d-flex.align-items-center {
  gap: 1rem;
}

@media (max-width: 576px) {
  .d-flex.justify-content-end {
    flex-direction: column;
    align-items: flex-end;
    gap: 1rem;
  }

  .mr-3 {
    margin-right: 0 !important;
    align-self: flex-end;
  }
}

.text-muted.small {
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }

  .text-muted.small {
    align-self: flex-start;
  }

  .d-flex.align-items-center {
    align-self: flex-end;
  }
}

.countdown-cell {
  font-weight: bold;
  color: #dc3545;
}

.countdown-cell.expired {
  color: #6c757d;
}

.countdown-cell {
  font-weight: bold;
  font-family: monospace;
  transition: all 0.3s ease;
}

.countdown-active {
  color: #dc3545;
  animation: pulse 2s infinite;
}

.countdown-pending {
  color: #007bff;
}

.countdown-expired {
  color: #6c757d;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
  }
}

.swal2-container {
  z-index: 2000 !important;
}

.swal2-popup {
  border-radius: 0.8rem !important;
  padding: 1.5rem !important;
}

.swal2-title {
  font-size: 1.25rem !important;
  color: #343a40 !important;
  font-weight: 600 !important;
}

.swal2-content {
  font-size: 1rem !important;
  color: #6c757d !important;
  margin-top: 0.5rem !important;
}

.swal2-actions {
  margin: 1.5rem 0 0 0 !important;
}

.swal2-confirm {
  background-color: #007bff !important;
  border-color: #007bff !important;
  padding: 0.5rem 1.5rem !important;
  border-radius: 50rem !important;
}

.swal2-cancel {
  background-color: transparent !important;
  color: #6c757d !important;
  border: 1px solid #ced4da !important;
  padding: 0.5rem 1.5rem !important;
  border-radius: 50rem !important;
}

.swal2-confirm:focus,
.swal2-cancel:focus {
  box-shadow: none !important;
}

.countdown-available {
  color: #28a745;
  font-weight: bold;
}

.filter-boom:disabled,
.custom-select:disabled {
  background-color: white !important;
  opacity: 0.7;
  cursor: not-allowed;
}

.filter-boom:disabled::placeholder,
.custom-select:disabled {
  color: #6c757d !important;
}

.filter-boom:disabled+.calendar-icon {
  opacity: 0.5;
  cursor: not-allowed;
}

.custom-select:disabled {
  background-color: white !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%236c757d' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
}

.filter-row-hidden {
  display: none;
}

/* .status-item {
  min-width: 115px !important;
  height: 34px !important;
  padding: 0 0.75rem !important;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  border-radius: 50rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  white-space: nowrap !important;
  line-height: 1 !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
  transition: all 0.2s ease !important;
  border: 1.5px solid !important;
} */

/* Ikon sama saiz & jarak */
.status-item .fas {
  font-size: 0.9em !important;
  margin-right: 0.35rem !important;
}

/* Hover hanya untuk butang BARU */
.status-item:hover:not(.badge) {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 123, 255, 0.2) !important;
}

/* Teks TIDAK DISAHKAN lebih kecil */
.small-text {
  font-size: 0.7rem !important;
}

/* Butang ulasan kecil */
.review-btn {
  height: 28px !important;
  width: 28px !important;
  padding: 0 !important;
  font-size: 0.75rem !important;
}

/* LEBAR SAMA UNTUK SEMUA STATUS */
/* .status-baru-btn,
.status-badge {
  min-width: 115px !important;
  max-width: 115px !important;
  width: 115px !important;
  height: 30px !important;
  padding: 0.2rem 0.4rem !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1 !important;
  border-radius: 50rem !important;
  transition: all 0.2s ease;
  box-sizing: border-box !important;
} */

/* BUTANG BARU (gaya asal) */
/* .status-baru-btn {
  height: 32px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  border: 1.5px solid #007bff !important;
} */

/* BADGE (gaya asal) */
/* .status-badge {
  font-size: 0.72rem !important;
  border: 1.5px solid transparent !important;
} */
/* Ikon sama saiz */
.status-baru-btn .fas,
.status-badge .fas {
  font-size: 0.85em !important;
  margin-right: 0.35rem !important;
}

.status-baru-btn .fas {
  font-size: 0.85em !important;
  margin-right: 0.35rem !important;
  color: #1565c0 !important;
}

/* Hover untuk butang */
.status-baru-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
}

.status-baru-btn:hover:not(:disabled) {
  background-color: #bbdefb !important;
  color: #0d47a1 !important;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(33, 150, 243, 0.25) !important;
}

/* Teks TIDAK DISAHKAN lebih kecil */
.badge-small-text {
  font-size: 0.70rem !important;
  padding: 0.2rem 0.4rem !important;
}

/* Butang ulasan */
.small-review-btn {
  height: 28px !important;
  width: 28px !important;
  padding: 0 !important;
  font-size: 0.7rem !important;
  margin-left: 0.5rem !important;
  border-radius: 50% !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #dc3545 !important;
  color: #dc3545 !important;
}

/* LEBAR SAMA 115px */
.status-baru-btn,
.status-badge {
  min-width: 160px !important;
  max-width: 160px !important;
  width: 160px !important;
  height: 30px !important;
  /* ← Dikurangkan dari 34px ke 30px */
  padding: 0.25rem 0.5rem !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2 !important;
  border-radius: 50rem !important;
  transition: all 0.2s ease;
  box-sizing: border-box !important;
  position: relative;
  flex-shrink: 0;
}

/* TINGGI RENDAH: 30px */
/* .status-baru-btn,
.status-badge {
  height: 30px !important;
  padding: 0.2rem 0.4rem !important;
  line-height: 1.1;
} */

/* FONT KECIL */
.status-baru-btn {
  min-width: 160px !important;
  max-width: 160px !important;
  width: 160px !important;
  height: 30px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2 !important;
  border-radius: 50rem !important;
  transition: all 0.2s ease;
  box-sizing: border-box !important;
  position: relative;
  flex-shrink: 0;

  background-color: #e3f2fd !important;
  color: #1565c0 !important;
  border: none !important;
  box-shadow: none !important;
}

.status-badge {
  border: 1.8px solid transparent !important;
}

/* Ikon lebih kecil & kemas */
/* .status-baru-btn .fas,
.status-badge .fas {
  font-size: 0.85em !important;
  margin-right: 0.2rem !important;
} */

/* Hover butang */
/* .status-baru-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
} */

.status-baru-btn:disabled {
  background-color: #f5f5f5 !important;
  color: #9e9e9e !important;
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Teks TIDAK DISAHKAN */
/* .badge-small-text {
  font-size: 0.68rem !important;
} */

/* Butang ulasan */
/* .small-review-btn {
  height: 26px !important;
  width: 26px !important;
  padding: 0 !important;
  font-size: 0.7rem !important;
} */
.status-baru-btn {
  background-color: rgba(203, 210, 246, 0.5) !important;
  color: #1e40af !important;
  border: 1.5px solid #93c5fd !important;
  transition: all 0.3s ease !important;
  min-width: 160px !important;
  max-width: 160px !important;
  width: 160px !important;
  height: 30px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2 !important;
  border-radius: 50rem !important;
  box-sizing: border-box !important;
  position: relative;
  flex-shrink: 0;
}

.status-baru-btn .fas {
  font-size: 0.85em !important;
  margin-right: 0.35rem !important;
  color: #1e40af !important;
}

.status-baru-btn:hover:not(:disabled) {
  background-color: rgba(203, 210, 246, 0.7) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3) !important;
}

.status-baru-btn:disabled {
  background-color: rgba(203, 210, 246, 0.3) !important;
  color: #93a3b5 !important;
  border-color: #c0d6f5 !important;
  cursor: not-allowed;
  opacity: 0.7;
  transform: none;
  box-shadow: none;
}

.status-baru-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f8f9fa !important;
  border-color: #ced4da !important;
  color: #6c757d !important;
}

.b-toaster {
  z-index: 9999 !important;
}

.b-toaster .b-toast {
  z-index: 9999 !important;
}

/* Gaya toast yang lebih menyerlah */
.success-toast-custom {
  background-color: #d4edda !important;
  border-left: 6px solid #28a745 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.success-toast-custom .toast-header {
  background-color: #d4edda !important;
  color: #155724 !important;
  font-weight: bold;
}

.success-toast-custom .toast-body {
  color: #155724 !important;
  font-weight: 500;
}

.swal2-popup-custom {
  border-radius: 0.8rem !important;
  padding: 1.5rem !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
}

.swal2-title-custom {
  font-size: 1.3rem !important;
  font-weight: 600 !important;
  color: #155724 !important;
}

.swal2-content-custom {
  font-size: 1rem !important;
  color: #155724 !important;
}

/* Butang hijau untuk success */
.btn-success {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
  padding: 0.5rem 1.5rem !important;
  border-radius: 50rem !important;
  font-weight: 500 !important;
}

.btn-success:hover {
  background-color: #218838 !important;
  border-color: #1e7e34 !important;
}
/* PAKSA BUTANG BAHARU DISABLED SAMA DENGAN ENABLED */
.status-baru-btn:disabled,
.status-baru-btn.disabled {
  background-color: rgba(203, 210, 246, 0.5) !important;
  color: #1e40af !important;
  border: 1.5px solid #93c5fd !important;
  opacity: 1 !important;
  cursor: not-allowed !important;
  transform: none !important;
  box-shadow: none !important;
  pointer-events: none !important;
}

/* Pastikan ikon dalam butang disabled juga sama */
.status-baru-btn:disabled .fas,
.status-baru-btn.disabled .fas {
  color: #1e40af !important;
}

/* Elakkan hover effect pada butang disabled */
.status-baru-btn:disabled:hover,
.status-baru-btn.disabled:hover {
  background-color: rgba(203, 210, 246, 0.5) !important;
  transform: none !important;
  box-shadow: none !important;
}
</style>
